import { computed, reactive, markRaw, shallowRef, type Component } from "vue";

/**
 * Globaler Store für alle offenen Dialog-Fenster (Taskbar).
 *
 * Jedes über `useDialogWindow` geöffnete Fenster registriert sich hier und
 * erscheint als Tab in der `MinimizedWindowDock`-Leiste (App.vue) – sichtbar
 * wie minimiert. Ein Klick auf den Tab minimiert ein sichtbares Fenster bzw.
 * stellt ein minimiertes wieder her.
 *
 * Zusätzlich verwaltet der Store die Stapelreihenfolge: `raiseDockWindow`
 * holt ein Fenster nach vorn. Die z-Indizes aller offenen Fenster werden
 * innerhalb des von Vuetify vergebenen Bandes neu verteilt
 * (`kleinster Vuetify-Wert + Position im Stapel`). Dadurch bleiben Menüs/
 * Selects, die Vuetify oberhalb dieses Bandes anlegt, immer über allen
 * Fenstern.
 *
 * Der z-Index wird reaktiv berechnet und von `useDialogWindow` als Style an
 * den `v-dialog` gegeben, sodass Vue ihn bei jedem Rendern selbst setzt.
 * Früher wurde er direkt am Overlay-Element überschrieben; Vuetify rendert
 * dort aber seinen eigenen z-Index und hat ihn beim nächsten Neuzeichnen
 * (z. B. nach Minimieren/Wiederherstellen) zurückgesetzt – das zuletzt
 * geöffnete Fenster lag dann immer vorn (Kundenfeedback 26.10.2026, I).
 */
export interface DockWindow {
  /** Eindeutige Fenster-ID, z. B. "werte-edit". */
  id: string;
  /** Unübersetzter Titel; die Dock-Komponente übersetzt via $gt. */
  title: string;
  /** Lucide-Icon des Dialogs (optional). */
  icon: Component | null;
  /** true, solange das Fenster als Chip geparkt (minimiert) ist. */
  minimized: boolean;
  /** Stellt das Fenster an seiner letzten Position wieder her. */
  restore: () => void;
  /** Parkt das Fenster in der Dock-Leiste. */
  minimize: () => void;
  /**
   * Von Vuetify beim Öffnen vergebener z-Index des Overlays; Untergrenze des
   * Bandes für die Stapelreihenfolge. `null`, bis er gemessen wurde.
   */
  baseZ?: number | null;
}

export interface DockState {
  windows: DockWindow[];
}

const state: DockState = reactive({ windows: [] });

/** Stapelreihenfolge (letzter Eintrag = oberstes Fenster). */
const stackOrder = shallowRef<string[]>([]);

export function useDockWindows(): DockState {
  return state;
}

/** Anzahl aktuell sichtbarer (nicht minimierter) Fenster. */
export const visibleWindowCount = computed(
  () => state.windows.filter((w) => !w.minimized).length,
);

/** Liefert eine neue Stapelreihenfolge mit `id` als oberstem Fenster. */
export function raiseInStack(order: readonly string[], id: string): string[] {
  return [...order.filter((sid) => sid !== id), id];
}

/**
 * Verteilt die z-Indizes gemäß Stapelreihenfolge: kleinster bekannter
 * Vuetify-Wert + Position im Stapel. Fenster ohne gemessenen Vuetify-Wert
 * bleiben außen vor (sie behalten den von Vuetify gesetzten Wert).
 */
export function computeStackZIndices(
  order: readonly string[],
  baseZById: Readonly<Record<string, number | null | undefined>>,
): Record<string, number> {
  const known = order.filter((id) => typeof baseZById[id] === "number");
  if (known.length === 0) return {};
  const minBase = Math.min(...known.map((id) => baseZById[id] as number));
  return Object.fromEntries(known.map((id, i) => [id, minBase + i]));
}

const stackZIndices = computed(() =>
  computeStackZIndices(
    stackOrder.value,
    Object.fromEntries(state.windows.map((w) => [w.id, w.baseZ ?? null])),
  ),
);

/** z-Index, den das Fenster gemäß Stapelreihenfolge erhalten soll (reaktiv). */
export function dockWindowZIndex(id: string): number | null {
  return stackZIndices.value[id] ?? null;
}

export function registerDockWindow(win: DockWindow): void {
  if (state.windows.some((w) => w.id === win.id)) return;
  state.windows = [
    ...state.windows,
    { ...win, baseZ: win.baseZ ?? null, icon: win.icon ? markRaw(win.icon) : null },
  ];
  stackOrder.value = raiseInStack(stackOrder.value, win.id);
}

export function unregisterDockWindow(id: string): void {
  state.windows = state.windows.filter((w) => w.id !== id);
  stackOrder.value = stackOrder.value.filter((sid) => sid !== id);
}

export function setDockWindowMinimized(id: string, minimized: boolean): void {
  state.windows = state.windows.map((w) => (w.id === id ? { ...w, minimized } : w));
}

/**
 * Merkt sich den von Vuetify vergebenen z-Index eines Fensters. Gilt einmal
 * je Öffnen; danach rendert das Fenster unseren Wert und ein erneutes Messen
 * würde nur ihn zurückliefern.
 */
export function setDockWindowBaseZ(id: string, baseZ: number): void {
  if (!state.windows.some((w) => w.id === id && w.baseZ == null)) return;
  state.windows = state.windows.map((w) => (w.id === id ? { ...w, baseZ } : w));
}

/** Holt ein Fenster in der Stapelreihenfolge nach vorn. */
export function raiseDockWindow(id: string): void {
  if (!state.windows.some((w) => w.id === id)) return;
  if (stackOrder.value.indexOf(id) === stackOrder.value.length - 1) return;
  stackOrder.value = raiseInStack(stackOrder.value, id);
}

/** Muss der Vuetify-Wert dieses Fensters noch gemessen werden? */
export function needsDockWindowBaseZ(id: string): boolean {
  return state.windows.some((w) => w.id === id && w.baseZ == null);
}
